import { Views } from './consts';
import { CameraViewName } from './types';
import { ViewerEvents } from '../Viewer';
import { ClassWatcher } from '../ClassWatcher/ClassWatcher';
const VIEW_CUBE_CONTAINER_CLASS = 'viewCubeContainer';
const VIEW_CUBE_CLASS = 'viewCube';
const CUBE_FACE_CLASS = 'viewCubeFace';
const FACE_PART_CLASS = 'facePart';
const FACE_PART_HOVERED_CLASS = 'facePartHovered';
const FACE_PART_SPAN_HOVERED_CLASS = 'hovered';
var CubeFace;
(function (CubeFace) {
    CubeFace["Front"] = "Front";
    CubeFace["Back"] = "Back";
    CubeFace["Left"] = "Left";
    CubeFace["Right"] = "Right";
    CubeFace["Top"] = "Top";
    CubeFace["Bottom"] = "Bottom";
})(CubeFace || (CubeFace = {}));
const CubeFaces = [
    CubeFace.Front,
    CubeFace.Back,
    CubeFace.Left,
    CubeFace.Right,
    CubeFace.Top,
    CubeFace.Bottom,
];
const viewCubeConfig = {
    [CubeFace.Front]: [
        CameraViewName.FrontBottomRight,
        CameraViewName.FrontBottom,
        CameraViewName.FrontBottomLeft,
        CameraViewName.FrontRight,
        CameraViewName.Front,
        CameraViewName.FrontLeft,
        CameraViewName.FrontTopRight,
        CameraViewName.FrontTop,
        CameraViewName.FrontTopLeft,
    ],
    [CubeFace.Back]: [
        CameraViewName.BackBottomLeft,
        CameraViewName.BackBottom,
        CameraViewName.BackBottomRight,
        CameraViewName.BackLeft,
        CameraViewName.Back,
        CameraViewName.BackRight,
        CameraViewName.BackTopLeft,
        CameraViewName.BackTop,
        CameraViewName.BackTopRight,
    ],
    [CubeFace.Left]: [
        CameraViewName.FrontBottomLeft,
        CameraViewName.BottomLeft,
        CameraViewName.BackBottomLeft,
        CameraViewName.FrontLeft,
        CameraViewName.Left,
        CameraViewName.BackLeft,
        CameraViewName.FrontTopLeft,
        CameraViewName.TopLeft,
        CameraViewName.BackTopLeft,
    ],
    [CubeFace.Right]: [
        CameraViewName.BackBottomRight,
        CameraViewName.BottomRight,
        CameraViewName.FrontBottomRight,
        CameraViewName.BackRight,
        CameraViewName.Right,
        CameraViewName.FrontRight,
        CameraViewName.BackTopRight,
        CameraViewName.TopRight,
        CameraViewName.FrontTopRight,
    ],
    [CubeFace.Top]: [
        CameraViewName.BackTopLeft,
        CameraViewName.BackTop,
        CameraViewName.BackTopRight,
        CameraViewName.TopLeft,
        CameraViewName.Top,
        CameraViewName.TopRight,
        CameraViewName.FrontTopLeft,
        CameraViewName.FrontTop,
        CameraViewName.FrontTopRight,
    ],
    [CubeFace.Bottom]: [
        CameraViewName.FrontBottomLeft,
        CameraViewName.FrontBottom,
        CameraViewName.FrontBottomRight,
        CameraViewName.BottomLeft,
        CameraViewName.Bottom,
        CameraViewName.BottomRight,
        CameraViewName.BackBottomLeft,
        CameraViewName.BackBottom,
        CameraViewName.BackBottomRight,
    ]
};
export class ViewCube {
    constructor(viewer) {
        this.facePartsByCameraView = new Map();
        this.classWatchers = [];
        this.viewer = viewer;
        this.onCameraChange = this.onCameraChange.bind(this);
        this.viewer.subscribe(ViewerEvents.CameraChanged, this.onCameraChange);
        this.build();
        this.updateViewCube(this.viewer.getCameraMatrices());
    }
    getRootElement() {
        return this.rootContainer;
    }
    dispose() {
        this.viewer.unsubscribe(ViewerEvents.CameraChanged, this.onCameraChange);
        this.classWatchers.forEach(w => w.disconnect());
    }
    build() {
        const rootContainer = document.createElement('div');
        rootContainer.className = VIEW_CUBE_CONTAINER_CLASS;
        const cubeContainer = document.createElement('div');
        cubeContainer.className = VIEW_CUBE_CLASS;
        rootContainer.append(cubeContainer);
        const faceContainers = CubeFaces.map((cubeFaceName) => this.createFace(cubeFaceName));
        cubeContainer.append(...faceContainers);
        this.addPartHoverCallbacks();
        this.rootContainer = rootContainer;
        this.cubeContainer = cubeContainer;
    }
    createFace(cubeFaceName) {
        const faceContainer = document.createElement('div');
        faceContainer.className = this.composeFaceClass(cubeFaceName);
        let middleFacePart = null;
        viewCubeConfig[cubeFaceName].forEach((view, index, arr) => {
            const facePart = document.createElement('div');
            facePart.className = FACE_PART_CLASS;
            facePart.addEventListener('click', () => this.setCameraView(view));
            this.registerPart(view, facePart);
            faceContainer.append(facePart);
            if (index === Math.floor(arr.length / 2)) {
                middleFacePart = facePart;
            }
        });
        const faceTextSpan = document.createElement('span');
        faceTextSpan.textContent = cubeFaceName.toString();
        if (middleFacePart) {
            const watcher = new ClassWatcher(middleFacePart, FACE_PART_HOVERED_CLASS, () => faceTextSpan.classList.add(FACE_PART_SPAN_HOVERED_CLASS), () => faceTextSpan.classList.remove(FACE_PART_SPAN_HOVERED_CLASS));
            watcher.observe();
            this.classWatchers.push(watcher);
        }
        faceContainer.append(faceTextSpan);
        return faceContainer;
    }
    composeFaceClass(face) {
        return `${CUBE_FACE_CLASS} ${CUBE_FACE_CLASS}${face}`;
    }
    setCameraView(viewName) {
        const view = Views[viewName];
        this.viewer.setCameraView(view);
    }
    registerPart(view, part) {
        const adjacentParts = this.facePartsByCameraView.get(view);
        if (adjacentParts) {
            adjacentParts.push(part);
        }
        else {
            this.facePartsByCameraView.set(view, [part]);
        }
    }
    addPartHoverCallbacks() {
        this.facePartsByCameraView.forEach((neighborParts) => {
            function onMouseEnter() {
                neighborParts.forEach((part) => {
                    part.classList.remove(FACE_PART_CLASS);
                    part.classList.add(FACE_PART_HOVERED_CLASS);
                });
            }
            function onMouseLeave() {
                neighborParts.forEach((part) => {
                    part.classList.remove(FACE_PART_HOVERED_CLASS);
                    part.classList.add(FACE_PART_CLASS);
                });
            }
            neighborParts.forEach((part) => {
                part.addEventListener('mouseenter', onMouseEnter);
                part.addEventListener('mouseleave', onMouseLeave);
            });
        });
    }
    composeCssMatrix(elements) {
        function epsilon(value, mEpsilon = 1e-10) {
            return Math.abs(value) < mEpsilon ? 0 : value;
        }
        return `matrix3d(
      ${epsilon(elements[0])}, ${epsilon(-elements[1])}, ${epsilon(elements[2])}, 0,
      ${epsilon(elements[4])}, ${epsilon(-elements[5])}, ${epsilon(elements[6])}, 0,
      ${epsilon(elements[8])}, ${epsilon(-elements[9])}, ${epsilon(elements[10])}, 0,
      0, 0, 0, 1)`;
    }
    updateViewCube(transform) {
        const cssMatrix = this.composeCssMatrix(transform.matrixWorldInverse);
        this.cubeContainer.style.setProperty('transform', cssMatrix);
    }
    onCameraChange(transform) {
        this.updateViewCube(transform.payload);
    }
}
